import { useState, useEffect, useCallback, useRef } from 'react'
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.min.mjs'
import { getFileRenderingError, isPasswordProtectedPdf, shouldTryImagePreview } from './previewErrors'

pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.legacy.mjs`

export const STATUS_MESSAGES: Record<
	string,
	{ message: string; showLoading?: boolean }
> = {
	FILE_TOO_BIG: {
		message: 'The file is too large and is not supported for previewing',
	},
	FILE_NOT_SUPPORTED_FORMAT: {
		message: "File type doesn't supported for preview",
	},
	FILE_PREVIEW_UNSUPPORTED_FORMAT: {
		message: "File type doesn't supported for preview",
	},
	FILE_THUMBNAIL_UNSUPPORTED_FORMAT: {
		message: "File type doesn't supported for preview",
	},
	PREVIEW_NOT_READY: {
		message: 'Preview is being generated, please wait...',
		showLoading: true,
	},
	THUMBNAIL_NOT_READY: {
		message: 'Thumbnail is being generated, please wait...',
		showLoading: true,
	},
	FILE_PREVIEW_NOT_READY: {
		message: 'Preview is being generated, please wait...',
		showLoading: true,
	},
	FILE_THUMBNAIL_NOT_READY: {
		message: 'Thumbnail is being generated, please wait...',
		showLoading: true,
	},
	FILE_PREVIEW_NOT_AVAILABLE: { message: 'File was created without content' },
	FILE_THUMBNAIL_NOT_AVAILABLE: {
		message: 'File was created without content',
	},
	error: { message: 'Failed to load preview' },
}

interface UseFileRenderingOptions {
	downloadUrl: string | null
	previewStatus: string
	isRenderingToPDF: boolean
	enabled?: boolean
	isMobile?: boolean
}

interface UseFileRenderingReturn {
	isRendering: boolean
	renderError: string | null
	isPasswordProtected: boolean
	unlockPdf: (password: string) => Promise<string | null>
	numPages: number | null
	fullPages: HTMLCanvasElement[]
	renderedPages: number[]
	pdfDocument: any | null
	renderPageBatch: (
		pdf: any,
		startIndex: number,
		endIndex: number,
	) => Promise<void>
	reset: () => void
}

export const useFileRendering = ({
	downloadUrl,
	previewStatus,
	isRenderingToPDF,
	enabled = true,
	isMobile = false,
}: UseFileRenderingOptions): UseFileRenderingReturn => {
	const isMobileRef = useRef(isMobile)
	isMobileRef.current = isMobile

	const [isRendering, setIsRendering] = useState(false)
	const [renderError, setRenderError] = useState<string | null>(null)
	const [isPasswordProtected, setIsPasswordProtected] = useState(false)
	const renderRequestRef = useRef(0)
	const loadingTaskRef = useRef<ReturnType<typeof pdfjs.getDocument> | null>(null)
	const [numPages, setNumPages] = useState<number | null>(null)
	const [fullPages, setFullPages] = useState<HTMLCanvasElement[]>([])
	const [renderedPages, setRenderedPages] = useState<number[]>([])
	const [pdfDocument, setPdfDocument] = useState<any>(null)
	const renderedPagesRef = useRef<Set<number>>(new Set())
	const inFlightPagesRef = useRef<Set<number>>(new Set())
	const pendingRangeRef = useRef<{
		startIndex: number
		endIndex: number
	} | null>(null)
	const isProcessingRenderRef = useRef(false)

	const releasePdfTask = useCallback(() => {
		const task = loadingTaskRef.current
		loadingTaskRef.current = null
		if (task) void task.destroy().catch((): void => undefined)
	}, [])

	const processLatestRenderRange = useCallback(async (pdf: any) => {
		if (isProcessingRenderRef.current) return
		const requestId = renderRequestRef.current
		isProcessingRenderRef.current = true

		try {
			while (pendingRangeRef.current) {
				const range = pendingRangeRef.current
				pendingRangeRef.current = null

				const pageCanvases: Array<{
					pageNumber: number
					canvas: HTMLCanvasElement
				}> = []
				const renderedNow: number[] = []

				const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
				const mobile = isMobileRef.current
				const MAX_PIXELS = isIOS || mobile ? 5_000_000 : 30_000_000

				const getSafeViewport = (page: any, desiredScale: number) => {
					const v = page.getViewport({ scale: desiredScale })
					const pixels = v.width * v.height
					if (pixels <= MAX_PIXELS) return v
					const k = Math.sqrt(MAX_PIXELS / pixels)
					return page.getViewport({ scale: desiredScale * k })
				}

				// Adaptive scale based on screen size
				let desiredScale = 1.5
				if (mobile) {
					desiredScale = 1
				} else if (typeof window !== 'undefined') {
					const screenWidth = window.innerWidth
					if (screenWidth >= 1920) {
						desiredScale = 1.8
					} else if (screenWidth >= 1440) {
						desiredScale = 1.6
					} else {
						desiredScale = 1.5
					}
				}
				const startPage = Math.max(1, range.startIndex + 1)
				const endPage = Math.min(range.endIndex, pdf.numPages)

				for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
					if (pendingRangeRef.current) {
						break
					}

					if (
						renderedPagesRef.current.has(pageNumber) ||
						inFlightPagesRef.current.has(pageNumber)
					) {
						continue
					}

					inFlightPagesRef.current.add(pageNumber)

					try {
						const page = await pdf.getPage(pageNumber)
						if (requestId !== renderRequestRef.current) return
						const viewport = getSafeViewport(page, desiredScale)
						const canvas = document.createElement('canvas')
						const context = canvas.getContext('2d')!

						canvas.width = Math.floor(viewport.width)
						canvas.height = Math.floor(viewport.height)

						await page.render({
							canvasContext: context,
							viewport: viewport,
						}).promise
						if (requestId !== renderRequestRef.current) return

						pageCanvases.push({ pageNumber, canvas })
						renderedNow.push(pageNumber)
					} catch (e) {
						if (requestId !== renderRequestRef.current) return
						console.error('[pdfjs] render failed', { pageNumber }, e)
						setRenderError(getFileRenderingError(e))
						pendingRangeRef.current = null
						releasePdfTask()
						break
					} finally {
						if (requestId === renderRequestRef.current) {
							inFlightPagesRef.current.delete(pageNumber)
						}
					}
				}

				if (pageCanvases.length > 0) {
					setFullPages((prev) => {
						const newPages = [...prev]
						pageCanvases.forEach(({ pageNumber, canvas }) => {
							newPages[pageNumber - 1] = canvas
						})
						return newPages
					})
				}

				if (renderedNow.length > 0) {
					setRenderedPages((prev) => {
						const merged = new Set([...prev, ...renderedNow])
						renderedPagesRef.current = merged
						return Array.from(merged).sort((a, b) => a - b)
					})
				}
			}
		} finally {
			if (requestId === renderRequestRef.current) {
				isProcessingRenderRef.current = false
				if (pendingRangeRef.current) {
					void processLatestRenderRange(pdf)
				}
			}
		}
	}, [releasePdfTask])

	const renderPageBatch = useCallback(
		async (pdf: any, startIndex: number, endIndex: number) => {
			pendingRangeRef.current = { startIndex, endIndex }
			void processLatestRenderRange(pdf)
		},
		[processLatestRenderRange],
	)

	const createFilePages = useCallback(
		async (url: string, password?: string): Promise<string | null> => {
			if (!url) return getFileRenderingError(null)
			const requestId = ++renderRequestRef.current
			const isCurrentRequest = () => requestId === renderRequestRef.current
			releasePdfTask()

			try {
				setIsRendering(true)
				if (password === undefined) setRenderError(null)
				setNumPages(null)
				setFullPages([])
				setRenderedPages([])
				setPdfDocument(null)

				try {
					loadingTaskRef.current = pdfjs.getDocument(
						password === undefined ? url : { url, password },
					)
					const pdf = await loadingTaskRef.current.promise
					if (!isCurrentRequest()) return null
					setRenderError(null)
					setIsPasswordProtected(false)
					setPdfDocument(pdf)
					setNumPages(pdf.numPages)
					setRenderedPages([])
					renderedPagesRef.current = new Set()
					inFlightPagesRef.current.clear()
					pendingRangeRef.current = null
					isProcessingRenderRef.current = false

					const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
					const firstBatch = isIOS || isMobileRef.current ? 2 : 10
					await renderPageBatch(pdf, 0, Math.min(firstBatch, pdf.numPages))
					if (!isCurrentRequest()) return null
					setIsRendering(false)
					return null
				} catch (error) {
					if (!isCurrentRequest()) return null
					releasePdfTask()
					if (!shouldTryImagePreview(error)) throw error
				}

				const img = new Image()
				img.crossOrigin = 'anonymous'

				img.onload = async () => {
					if (!isCurrentRequest()) return
					try {
						const canvas = document.createElement('canvas')
						const ctx = canvas.getContext('2d')!

						canvas.width = img.naturalWidth || img.width
						canvas.height = img.naturalHeight || img.height

						ctx.drawImage(img, 0, 0)

						setNumPages(1)
						setFullPages([canvas])
						setRenderedPages([1])
						setPdfDocument(null)
					} catch (err) {
						console.error('Error rendering image:', err)
						setRenderError(getFileRenderingError(err))
					} finally {
						setIsRendering(false)
					}
				}

				img.onerror = () => {
					if (!isCurrentRequest()) return
					setRenderError(getFileRenderingError(null))
					setIsRendering(false)
				}

				img.src = url
				return null
			} catch (err) {
				if (!isCurrentRequest()) return null
				const message = getFileRenderingError(err)
				setIsPasswordProtected(isPasswordProtectedPdf(err))
				setRenderError(message)
				setIsRendering(false)
				return message
			}
		},
		[renderPageBatch, releasePdfTask],
	)

	const reset = useCallback(() => {
		renderRequestRef.current += 1
		releasePdfTask()
		setIsRendering(false)
		setRenderError(null)
		setIsPasswordProtected(false)
		setNumPages(null)
		setFullPages([])
		setRenderedPages([])
		setPdfDocument(null)
		renderedPagesRef.current = new Set()
		inFlightPagesRef.current.clear()
		pendingRangeRef.current = null
		isProcessingRenderRef.current = false
	}, [releasePdfTask])

	useEffect(() => {
		reset()
		if (!enabled) return
		if (isRenderingToPDF) return
		if (downloadUrl && previewStatus === 'ready') {
			createFilePages(downloadUrl)
		}
		return () => {
			renderRequestRef.current += 1
			releasePdfTask()
		}
	}, [downloadUrl, previewStatus, isRenderingToPDF, enabled, createFilePages, reset, releasePdfTask])

	const unlockPdf = useCallback(async (password: string) => {
		if (!enabled || !downloadUrl || previewStatus !== 'ready') {
			return getFileRenderingError(null)
		}
		return createFilePages(downloadUrl, password)
	}, [enabled, downloadUrl, previewStatus, createFilePages])

	return {
		isRendering,
		renderError,
		isPasswordProtected,
		unlockPdf,
		numPages,
		fullPages,
		renderedPages,
		pdfDocument,
		renderPageBatch,
		reset,
	}
}
